import { ZoomInIcon, ZoomOutIcon } from '@langchain/untitled-ui-icons';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';

import { RunDetailsFilterBar } from '@/components/FilterBar/FilterBar';
import { Button, IconButton, Text } from '@/design-system';
import InfoCircleIcon from '@/icons/InfoCircleIcon.svg?react';
import { RunSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { WaterfallZoomProvider } from '../store/WaterfallZoomContext';
import { useWaterfallZoomState } from '../store/useWaterfallZoomState';
import { useDetectXSPanel } from '../utils/useDetectXSPanel';
import { useRunsInTreeStructure } from '../utils/useRunsInTreeStructure';
import { useUserPrefs } from '../utils/useUserPrefs';
import { EMPTY_LIST } from '../utils/utils';
import RunDetailsNavigationHeader from './RunDetailsNavigationHeader';
import { RunTreeNode } from './Tree';
import { WaterfallLatencyGraph } from './WaterfallLatencyGraph';

const pluralRules = new Intl.PluralRules('en-US');

function RunDetailsFilteredHiddenDisclaimer(props: {
  hiddenCount: number;
  filterCount: number;
  onClick: () => void;
}) {
  if (props.filterCount > 0) {
    return (
      <Text variant="sm" as="span">
        <Button
          variant="underlined"
          color="secondary"
          size="sm"
          onClick={props.onClick}
        >
          Clear filters
        </Button>{' '}
        to show {props.hiddenCount} hidden runs
      </Text>
    );
  }

  return (
    <>
      <InfoCircleIcon className="h-4 w-4 flex-shrink-0" />
      <Text variant="sm">
        {props.filterCount === 0
          ? 'No runs found.'
          : 'Some runs have been hidden.'}{' '}
        <Button
          variant="underlined"
          color="secondary"
          size="sm"
          onClick={props.onClick}
        >
          Clear filters
        </Button>
      </Text>
    </>
  );
}

function RunDetailsHiddenDisclaimer(props: {
  hiddenCount: number;
  onClick: () => void;
}) {
  if (props.hiddenCount === 0) return null;
  return (
    <>
      <InfoCircleIcon className="h-4 w-4 flex-shrink-0" />

      <span className="text-xs">
        Some runs have been hidden.{' '}
        <a className="cursor-pointer underline" onClick={props.onClick}>
          Show {props.hiddenCount}{' '}
          {
            {
              one: 'hidden run',
              two: 'hidden runs',
              few: 'hidden runs',
              other: 'hidden runs',
            }[pluralRules.select(props.hiddenCount)]
          }
        </a>
      </span>
    </>
  );
}

function RunDetailsFooter(props: {
  hiddenCount: number;
  filterCount: number;

  visibility: 'all' | 'filtered' | 'most_relevant';
  filteredOnly: boolean;
  isXsPanel: boolean;

  onSearchFilterChange: (filter: string | undefined) => void;
  setVisibility: (value: 'all' | 'filtered' | 'most_relevant') => void;
}) {
  const {
    hiddenCount,
    filterCount,
    visibility,
    filteredOnly,
    isXsPanel,
    onSearchFilterChange,
    setVisibility,
  } = props;

  if (visibility === 'all' || isXsPanel) {
    return null;
  }

  return (
    <div className="sticky bottom-0 z-10 flex items-center gap-2.5 bg-background px-4 py-3 text-tertiary empty:hidden">
      {filteredOnly ? (
        <RunDetailsFilteredHiddenDisclaimer
          hiddenCount={hiddenCount}
          filterCount={filterCount}
          onClick={() => onSearchFilterChange(undefined)}
        />
      ) : (
        <RunDetailsHiddenDisclaimer
          hiddenCount={hiddenCount}
          onClick={() => setVisibility('all')}
        />
      )}
    </div>
  );
}

export function WaterfallZoomButtons() {
  const zoomIn = useWaterfallZoomState((state) => state.zoomIn);
  const zoomOut = useWaterfallZoomState((state) => state.zoomOut);
  const resetZoom = useWaterfallZoomState((state) => state.resetZoom);
  const canZoomOut = useWaterfallZoomState(
    (state) => state.zoomWidth > state.containerWidth
  );
  const canZoomIn = useWaterfallZoomState(
    (state) => state.zoomWidth < 10 * state.containerWidth
  );

  return (
    <div className="flex items-center justify-end gap-1 pr-2">
      <Button size="sm" onClick={resetZoom} variant="plain" color="secondary">
        Reset
      </Button>

      <IconButton
        size="sm"
        icon={ZoomInIcon}
        onClick={zoomIn}
        disabled={!canZoomIn}
        variant="outlined"
        color="secondary"
        label="Zoom in"
      />

      <IconButton
        size="sm"
        icon={ZoomOutIcon}
        disabled={!canZoomOut}
        onClick={zoomOut}
        variant="outlined"
        color="secondary"
        label="Zoom out"
      />
    </div>
  );
}

export function WaterfallToggleButton(props: {
  showGraphStats: boolean;
  toggleGraphStats: (show: boolean) => void;
  isXsPanel: boolean;
  disabled?: boolean;
}) {
  const { showGraphStats, toggleGraphStats, isXsPanel } = props;
  if (isXsPanel) {
    return (
      <div
        className="flex cursor-pointer items-center gap-1 rounded-md px-2 py-1.5 hover:bg-secondary-hover"
        onClick={() => toggleGraphStats(!showGraphStats)}
      >
        <Text variant="sm">Waterfall</Text>
      </div>
    );
  }
  return (
    <Button
      size="sm"
      variant={showGraphStats ? 'normal' : 'outlined'}
      color={showGraphStats ? 'primary' : 'secondary'}
      onClick={() => toggleGraphStats(!showGraphStats)}
      className={cn(isXsPanel ? 'w-full' : 'flex-none self-center')}
      disabled={props.disabled}
    >
      Waterfall
    </Button>
  );
}

function RunDetailsNavigation(props: {
  hideHeader?: boolean;
  hideFooter?: boolean;
  isPublicPage: boolean;
  run: RunSchema;
  rootRuns: RunSchema[];

  searchFilter: string | undefined;
  onSearchFilterChange: (filter: string | undefined) => void;
  filteredRuns: RunSchema[] | undefined;

  childRunLookup: Record<string, RunSchema[] | undefined>;
  onNavigate: (run: RunSchema) => void;
  togglePanelSize: () => void;
  insidePane?: boolean;
  comparePosition?: 'left' | 'right';
  showAllRuns?: boolean;

  userPrefs: ReturnType<typeof useUserPrefs>;
  toggleGraphStats: (show: boolean) => void;
  isXsPanel: boolean;
}) {
  const { rootRuns, userPrefs, isXsPanel } = props;

  const [foldedRuns, setFoldedRuns] = useState<string[]>(EMPTY_LIST);

  const {
    filteredOnly,
    userAllRuns,
    showGraphStats,
    setUserFilteredOnly,
    setUserAllRuns,
    filterCount,
  } = userPrefs;

  const treeState = useRunsInTreeStructure({
    rootRuns,
    run: props.run,
    childRunLookup: props.childRunLookup,
    filteredRuns: props.filteredRuns,
    filteredOnly,
    userAllRuns,
    foldedRuns,
  });

  const { tree, hiddenCount, childRunLookupWithFolds } = treeState;

  const treeVirtuosoRef = useRef<VirtuosoHandle | null>(null);

  // handle stale folded runs state when switching runs via pane
  // See https://github.com/langchain-ai/langchainplus/pull/1170/commits/e816ce7c52819386d7aa8e4ba6cf0992dbec495d
  useEffect(() => {
    setFoldedRuns((prev) => {
      const next = prev.filter(
        (r) => !(props.run.parent_run_ids ?? []).includes(r)
      );
      if (next.length !== prev.length) {
        return next;
      } else {
        return prev;
      }
    });
  }, [props.run.parent_run_ids]);

  const { flattenedRuns, depthMetaList } = tree;

  const allRuns = userAllRuns || hiddenCount === 0;

  const visibility = filteredOnly
    ? 'filtered'
    : allRuns
    ? 'all'
    : 'most_relevant';

  const setVisibility = (value: 'all' | 'filtered' | 'most_relevant') => {
    setUserFilteredOnly(value === 'filtered');
    setUserAllRuns(value === 'all');
  };

  // On initial mount, scroll to the run that's focused
  // Must make sure flattenRuns and props.run are available in first render
  useLayoutEffect(() => {
    const handleScrollIntoView = (runId: string) => {
      if (treeVirtuosoRef.current) {
        const idx = flattenedRuns.findIndex((r) => r.run.id === runId);
        treeVirtuosoRef.current.scrollToIndex({
          index: idx,
          align: 'center',
          behavior: 'smooth',
        });
      }
    };
    // need this time out because virtuoso ref isn't available immediately
    setTimeout(() => {
      handleScrollIntoView(props.run.id);
    }, 100);
  }, []);

  const panelId = props.comparePosition
    ? `trace-panel-${props.comparePosition}`
    : 'trace-panel';

  const hideTitles = useDetectXSPanel({ panelId, breakpoint: 130 });
  return (
    <WaterfallZoomProvider>
      <div className="flex h-full flex-col" id={panelId}>
        {!props.hideHeader && (
          <RunDetailsNavigationHeader
            hiddenCount={hiddenCount}
            filterCount={filterCount}
            visibility={props.showAllRuns ? 'all' : visibility}
            disableFiltering={props.showAllRuns}
            setVisibility={setVisibility}
            foldedRuns={foldedRuns}
            toggleFoldAllRuns={() => {
              if (foldedRuns.length > 0) {
                setFoldedRuns([]);
              } else {
                // fold everything but root run
                const allRunIds = Object.keys(props.childRunLookup);
                setFoldedRuns(
                  allRunIds.filter(
                    (id) => !rootRuns.map((r) => r.id).includes(id)
                  )
                );
              }
            }}
            childRunLookup={props.childRunLookup}
            insidePane={props.insidePane}
            isXsPanel={isXsPanel}
            togglePanelSize={props.togglePanelSize}
            additionalFilters={
              <div className={cn('flex', isXsPanel ? 'flex-col' : 'gap-1')}>
                {/* TODO: Add this back in a separate PR */}
                {!props.isPublicPage && (
                  <RunDetailsFilterBar
                    rootRuns={rootRuns}
                    onChange={({ filter }) => {
                      if (filter) {
                        setVisibility('filtered');
                      }
                      props.onSearchFilterChange(filter);
                    }}
                    value={{ filter: props.searchFilter }}
                    disabled={props.showAllRuns}
                    buttonVariant={isXsPanel ? 'inline' : 'button'}
                  />
                )}
                <WaterfallToggleButton
                  showGraphStats={showGraphStats}
                  toggleGraphStats={props.toggleGraphStats}
                  isXsPanel={isXsPanel}
                />
              </div>
            }
            comparing={!!props.comparePosition}
            right={showGraphStats && <WaterfallZoomButtons />}
            isPublicPage={props.isPublicPage}
          />
        )}

        {props.run &&
          rootRuns.length > 0 &&
          props.childRunLookup &&
          (showGraphStats ? (
            <>
              <WaterfallLatencyGraph
                treeState={treeState}
                activeRun={props.run}
                onNavigate={props.onNavigate}
                setFoldedRuns={setFoldedRuns}
                filteredRuns={props.filteredRuns}
              />
              <RunDetailsFooter
                hiddenCount={hiddenCount}
                filterCount={filterCount}
                visibility={visibility}
                filteredOnly={filteredOnly}
                isXsPanel={isXsPanel}
                onSearchFilterChange={props.onSearchFilterChange}
                setVisibility={setVisibility}
              />
            </>
          ) : (
            <Virtuoso
              id="tree-panel-virtuoso"
              ref={treeVirtuosoRef}
              data={flattenedRuns}
              defaultItemHeight={32}
              className="[&_[data-viewport-type=element]>div:last-of-type]:sticky [&_[data-viewport-type=element]>div:last-of-type]:bottom-0"
              itemContent={(idx, { depth, run, isActiveDescendant }) => {
                const childRuns = childRunLookupWithFolds?.[run.id];
                return (
                  <RunTreeNode
                    scrollIdx={idx}
                    scrollRef={treeVirtuosoRef}
                    run={run}
                    depth={depth}
                    depthMeta={depthMetaList[idx]}
                    activeRunId={props.run.id}
                    rootRun={rootRuns[0]}
                    isActiveDescendant={isActiveDescendant}
                    isPublicPage={props.isPublicPage}
                    onClick={props.onNavigate}
                    setFoldedRuns={setFoldedRuns}
                    hasChildren={Array.isArray(childRuns)}
                    hasChildrenVisible={
                      Array.isArray(childRuns) && childRuns.length > 0
                    }
                    isFlatStructure={isXsPanel}
                    focused={!!props.filteredRuns?.find((i) => i.id === run.id)}
                    hideTitles={hideTitles}
                  />
                );
              }}
              components={{
                Footer: !props.hideFooter
                  ? () => (
                      <RunDetailsFooter
                        hiddenCount={hiddenCount}
                        filterCount={filterCount}
                        visibility={visibility}
                        filteredOnly={filteredOnly}
                        isXsPanel={isXsPanel}
                        onSearchFilterChange={props.onSearchFilterChange}
                        setVisibility={setVisibility}
                      />
                    )
                  : undefined,
              }}
            />
          ))}
      </div>
    </WaterfallZoomProvider>
  );
}

export { RunDetailsNavigation };
